import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { errorMessage, useMonitoringBb, useMonitoringMetrics } from "../api";
import { ErrorBlock, LoadingBlock } from "../components/primitives";
import { useMonitoring } from "../monitoring-context";
import { BbBranchesCard, BbOtherBranchesCard } from "./bb/branches";
import { BbOutcomeChart, BbVolumeChart } from "./bb/charts";
import { BbTotals } from "./bb/summary";
import { BbPizzaTimelineSheet } from "./bb/timeline";
import { BbUncapturedCard } from "./bb/uncaptured";
import { BbWebhookLogCard } from "./bb/webhook-log";
import { MobileApiLogCard } from "./bb/mobile-log";
import { branchLookup } from "./pipeline/helpers";

const ITEM_PARAM = "bb_item";

/**
 * BiteBerry: what BB sends (order / item / status webhooks), what the backend decided for each, why
 * finished pizzas have no photo, and what the tablet app asks the mobile API and is answered. The pizza timeline panel is in the URL (bb_item) so it can be shared.
 */
export function BbTab() {
  const { window } = useMonitoring();
  const bb = useMonitoringBb();
  const metrics = useMonitoringMetrics();
  const [params, setParams] = useSearchParams();
  const data = bb.data;
  const branchOf = useMemo(() => branchLookup(data?.branches_meta), [data?.branches_meta]);
  const itemId = params.get(ITEM_PARAM);

  const setItem = useCallback(
    (id: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (id) next.set(ITEM_PARAM, id);
          else next.delete(ITEM_PARAM);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const open = useCallback((id: string) => setItem(id), [setItem]);
  const close = useCallback(() => setItem(null), [setItem]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {bb.isError && <ErrorBlock message={errorMessage(bb.error)} onRetry={() => void bb.refetch()} />}
      {data ? (
        <>
          <BbTotals data={data} metrics={metrics} />
          <BbUncapturedCard data={data} branchOf={branchOf} onOpen={open} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <BbVolumeChart query={metrics} />
            <BbOutcomeChart data={data} />
          </div>
          <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <BbBranchesCard data={data} />
            <BbOtherBranchesCard data={data} />
          </div>
        </>
      ) : (
        !bb.isError && (
          <>
            <LoadingBlock height={84} />
            <LoadingBlock height={300} />
          </>
        )
      )}
      <BbWebhookLogCard window={window} branches={data?.branches_meta ?? []} onOpen={open} />
      <MobileApiLogCard window={window} onOpen={open} />
      <BbPizzaTimelineSheet itemId={itemId} onClose={close} />
    </div>
  );
}
